import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ButtonBase from '@material-ui/core/ButtonBase';
import { loginByPinCode, updateToken } from 'src/api/api';
import { unavailableAccountsMessage, updateAccountTokens } from 'src/api/accountTokens';
import BtnLink from 'src/components/commons/Buttons/BtnLink/BtnLink';
import Alert from 'src/components/commons/Alert/Alert';
import {
    initConnectSuccess,
    setErrorMessage,
    setNeedsPrivacyPolicy,
    setPinCode,
    setPrivacyPolicy,
} from 'src/store/actions/loginAction';
import { setIsRedirectPopupOpenAction } from 'src/store/slices/common/commonSlice';
import { useHistory } from 'react-router-dom';
import { routesEndpoints } from 'src/routes/routes_endpoints';
import { ILoginState, PrivacyPolicy } from 'src/store/type';
import { IAccount, ResPinCodeLogin, SingupType } from 'src/types/login';
import { FcData, ReqStatus } from 'src/api/types';
import { getLocalStorageValue } from 'src/utilis/utilis';
import { StorageKeys } from 'src/types/global';
import { v4 as GuidService } from 'uuid';
import Card from '../../components/Card/Card';
import Container from '../../components/Container/Container';
import Title from '../../components/Title/Title';
import Code from '../../Signup/InitialIdentification/Code/Code';
import styles from './PinCode.style';

interface Props { }

const PinCode = (props: Props) => {
    const { t } = useTranslation();
    const history = useHistory();
    const disptach = useDispatch();
    const onSetLoginData = (payload: ILoginState) => disptach(initConnectSuccess(payload));
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [keyCode, setKeyCode] = useState<string>(GuidService());
    const [errorAlertText, setErrorAlertText] = useState<string>('');
    const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
    const setErrorAlertTextRef = useRef(setErrorAlertText);
    setErrorAlertTextRef.current = setErrorAlertText;

    const TOAST_DURATION_S = 6;

    useEffect(() => {
        if (!lockedUntil) return;
        let ticks = 0;
        const interval = setInterval(() => {
            ticks++;
            const remaining = lockedUntil.getTime() - Date.now();
            if (remaining <= 0 || ticks >= TOAST_DURATION_S) {
                clearInterval(interval);
                setLockedUntil(null);
                return;
            }
            const minutes = Math.floor(remaining / 60000);
            const seconds = Math.floor((remaining % 60000) / 1000);
            const timeLeft = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
            setErrorAlertTextRef.current(`${t('smsPunishmentLocked')} ${timeLeft} ${t('minutes')}`);
            setShowErrorAlert(true);
        }, 1000);
        return () => clearInterval(interval);
    }, [lockedUntil, t]);

    const onSubmit = async (code: string) => {
        try {
        const deviceToken = getLocalStorageValue(StorageKeys.DeviceToken);

        const res = await loginByPinCode({
            deviceToken,
            pinCode: code,
        });

        if (res.response === ResPinCodeLogin.SUCCESS) {
            // const payload = {}
            const { data = [] } = res;

            const loginData: FcData = data[0] || {};
            const fcAccounts: IAccount[] = loginData.fcAccounts || [];
            const token: string = loginData.token || '';
            const indexerR: string = loginData.indexerR || '';

            const { available, unavailable } = await updateAccountTokens(
                fcAccounts,
                ({ proxyUrl, contactUUID, dbUrl, proxyToken }) =>
                    updateToken({ token, proxyUrl, dbUrl, contactUUID, indexerR, accessToken: proxyToken?.accessToken || '', tokenType: proxyToken?.tokenType || 'Bearer' })
            );
                /*
                get default account's client fron local storage
                if client is on fc accounts list, that means its the default account

                updatetoken.then:
                    if is default account:
                        onSetLoginData({
                        loginType: SingupType.PinCode,
                        isAuthenticated: ReqStatus.SUCCESS,
                        selectedFcAccount: fcAccounts[i],
                        ...data[i],
                        });
                        history.push(`${routesEndpoints.HOME}`);
                    else:
                        return
                */

            if (fcAccounts.length && !available.length) {
                setErrorAlertText(t('allCompaniesUnavailable'));
                setShowErrorAlert(true);
                setKeyCode(GuidService());
                return;
            }
            if (unavailable.length) disptach(setErrorMessage(unavailableAccountsMessage(unavailable, t)));

            onSetLoginData({
                loginType: SingupType.PinCode,
                isAuthenticated: ReqStatus.SUCCESS,
                selectedFcAccount: available[0],
                ...data[0],
                fcAccounts: available,
            });
            history.push(`${routesEndpoints.HOME}`);
        } else if (res.response === ResPinCodeLogin.PRIVACY_POLICY_NOT_APPROVED) {
            const privacyPolicy: PrivacyPolicy = res.data as PrivacyPolicy;

            // history.push('signup-options');
            disptach(setPinCode(code));
            disptach(setPrivacyPolicy(privacyPolicy));
            disptach(setNeedsPrivacyPolicy(true));

        } else if (String(res.response) === ResPinCodeLogin.TOO_MANY_ATTEMPTS_LOCKED) {
            const lockedUntilUTC = (res.data as any)?.lockedUntilUTC;
            if (lockedUntilUTC) {
                const lockDate = new Date(lockedUntilUTC);
                const remaining = lockDate.getTime() - Date.now();
                const minutes = Math.floor(remaining / 60000);
                const seconds = Math.floor((remaining % 60000) / 1000);
                const timeLeft = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                setErrorAlertText(`${t('smsPunishmentLocked')} ${timeLeft} ${t('minutes')}`);
                setLockedUntil(lockDate);
            } else {
                setErrorAlertText(t('wrongCodeToManyTimes'));
            }
            setShowErrorAlert(true);
            setKeyCode(GuidService());
        }
        else if (res.response === ResPinCodeLogin.WRONG_PIN_CODE) {
            setErrorAlertText(t('wrongCode'));
            setShowErrorAlert(true);
            setKeyCode(GuidService());
        } else {
            setErrorAlertText(t('wrongCode'));
            setShowErrorAlert(true);
            setKeyCode(GuidService());
        }
        } catch (error: any) {
            if (error?.response?.status === 401) {
                disptach(setIsRedirectPopupOpenAction({ isRedirectPopupOpen: true, reason: 'refresh_failed' }));
            } else {
                setErrorAlertText(t('wrongCode'));
                setShowErrorAlert(true);
            }
            console.log('e', error);
        }
    };

    const navigateToSinupOptions = () => {
        history.push(`${routesEndpoints.ROOT_AUTH}${routesEndpoints.AUTH.SINGUP_OPTIONS}`);
    };

    const onPressForgetDetailes = () => {
        history.push(`${routesEndpoints.ROOT_AUTH}${routesEndpoints.AUTH.SINGUP}`);
    };

    const TitleComponent = <styles.Text>{t('enterPinCode')}</styles.Text>;

    const handleCloseSnack = () => setShowErrorAlert(false);
    const isAutoSubmit = false;

    return (
        <Container>
            <Title>{t('login')}</Title>
            <Card>
                <styles.SpaceAround>
                    <Code
                        key={keyCode}
                        isInputSecure
                        isAutoSubmit={isAutoSubmit}
                        onSubmit={onSubmit}
                        Title={TitleComponent}
                        btnText={t('connect')}
                    />
                </styles.SpaceAround>
                <styles.Bootom>
                    <ButtonBase onClick={onPressForgetDetailes}>
                        <styles.BootomText>{t('forgotPasswordText')}</styles.BootomText>
                    </ButtonBase>
                </styles.Bootom>
            </Card>
            {/* <BtnLink
            btnText={t('anotherSignup')}
            onClick={navigateToSinupOptions}
         /> */}

            <Alert open={showErrorAlert} onClose={handleCloseSnack} severity="error">
                {errorAlertText}
            </Alert>
        </Container>
    );
};

export default PinCode;
